// Delivery planning for the dispatch map.
//
// Given a set of sites (each with lat/lng, a `product`, and a `fillAmount` =
// litres of that product to deliver) and a fleet of trucks, this groups the
// sites into truck loads that stay geographically compact while respecting the
// hard operational limits.
//
// TWO KINDS OF TRUCK
// A fleet tier is either:
//
//   { mode: 'tanker', capacity: 30000, compartments: 3, count: 2 }
//   { mode: 'deck',   toteSlots: 8, count: null }
//
// A **tanker** has fixed shells, so every compartment is the same size:
// `capacity / compartments`. A **deck** truck carries IBC totes, and a tote's
// volume comes from the Supply step and differs per product — methanol in
// 1,000 L totes alongside glycol in 640 L totes is normal. An IBC footprint is
// the same either way, so a deck truck's capacity is a *slot count*, not litres.
//
// Both collapse to the same rule once each tier is given a per-product size:
//
//   slots needed = Σ over products of ceil(productLoad_p / size_p)
//   truck is legal when slots ≤ slotBudget   (slotBudget = slots × maxTripsPerTruck)
//   trips        = ceil(slots / slots-per-run)
//
// where `size_p` is `capacity / compartments` for every product on a tanker, and
// that product's tote size on a deck. A product with no tote size cannot ride a
// deck truck at all (its size is 0, so it needs infinite slots) — it can only go
// on a tanker, and if the fleet has none it comes back unplaced.
//
// With one tanker tier, one compartment and a single product this is
// arithmetically identical to the original pooled-capacity rule, so old
// single-product dispatches plan exactly as they always did.
//
// A blank/null/zero `count` means "as many as needed" (unlimited). A bare number
// for the whole fleet is read as one unlimited single-compartment tanker.
//
// Distances are true great-circle kilometres (haversine).
//
// Quality: a single greedy pass leaves scattered "leftover" clusters, so we:
//   1. Build several times (multi-start) with different seeds.
//   2. Improve each build with a local search (relocate + swap moves) that only
//      keeps moves which reduce total compactness (sum of each site's distance
//      to its truck centre) while respecting all the hard caps.
//   3. Keep the best-scoring result — fewest stranded sites first, then tightest.
// The `optimizeEffort` option (0–100) trades speed for tighter groupings.

const TRUCK_COLORS = [
  '#e8a838', '#388bd6', '#2ec48c', '#d6685a',
  '#b86fc9', '#4fb3bf', '#c9a227', '#7e8bd6',
];

const EARTH_RADIUS_KM = 6371;
// How many candidate clusters to consider per relocate/swap (nearest by centre).
const RELOCATE_CANDIDATES = 6;
const SWAP_CANDIDATES = 4;
// Litres below which a residue is treated as empty, and the slack allowed when
// asking "does this load divide evenly into slots" so that floating-point drift
// can't invent an extra one.
const EPS = 1e-9;

export const NO_PRODUCT = 'No product';
export const TANKER = 'tanker';
export const DECK = 'deck';

function toNum(v) {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

// Blank/missing products all collapse to one bucket so they still occupy a
// compartment rather than silently merging with a real product.
export function normalizeProduct(value) {
  const name = String(value == null ? '' : value).trim();
  return name || NO_PRODUCT;
}

// Great-circle distance in kilometres.
function haversineKm(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

// Deterministic PRNG (mulberry32) so the same inputs give the same plan.
function makeRng(seed) {
  let a = seed >>> 0;
  return function rng() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function centroidOfIdx(sites, idx) {
  let lat = 0;
  let lng = 0;
  for (const i of idx) {
    lat += sites[i].lat;
    lng += sites[i].lng;
  }
  return { lat: lat / idx.length, lng: lng / idx.length };
}

// Sum of each member's distance to the cluster centre (our compactness cost).
function clusterCost(sites, idx) {
  if (idx.length <= 1) return 0;
  const c = centroidOfIdx(sites, idx);
  let sum = 0;
  for (const i of idx) sum += haversineKm(sites[i], c);
  return sum;
}

function radiusOk(sites, idx, maxRadiusKm) {
  if (maxRadiusKm === Infinity || idx.length <= 1) return true;
  const c = centroidOfIdx(sites, idx);
  for (const i of idx) {
    if (haversineKm(sites[i], c) > maxRadiusKm) return false;
  }
  return true;
}

// ---- Slots ----

// Slots one product's load occupies given the size of a slot on this truck.
// A size of zero means the truck cannot carry that product at all, which the
// Infinity makes every fit test reject.
function slotsFor(load, size) {
  if (load <= EPS) return 0;
  if (!(size > 0)) return Infinity;
  return Math.ceil(load / size - EPS);
}

// Slots a whole per-product load vector occupies.
function slotsOf(loads, sizes) {
  let n = 0;
  for (let p = 0; p < loads.length; p++) {
    n += slotsFor(loads[p], sizes[p]);
    if (n === Infinity) return Infinity;
  }
  return n;
}

function makeCluster(slot, productCount) {
  return {
    idx: [],
    load: 0,
    loads: new Array(productCount).fill(0),
    slots: 0,
    mode: slot.mode,
    capacity: slot.capacity,
    compartments: slot.compartments,
    toteSlots: slot.toteSlots,
    sizes: slot.sizes,
    slotsPerRun: slot.slotsPerRun,
    slotBudget: slot.slotBudget,
    cap: slot.cap,
  };
}

// Loads and the cached slot count move together — every mutation goes through
// here so `slots` can never drift out of step with `loads`.
function addLoad(c, p, amount) {
  const size = c.sizes[p];
  c.slots -= slotsFor(c.loads[p], size);
  c.loads[p] += amount;
  c.slots += slotsFor(c.loads[p], size);
  c.load += amount;
}

function removeLoad(c, p, amount) {
  addLoad(c, p, -amount);
}

// Would adding `amount` litres of product `p` keep this truck legal? O(1) — only
// product p's slot count can change.
function canAdd(c, p, amount) {
  const size = c.sizes[p];
  const delta = slotsFor(c.loads[p] + amount, size) - slotsFor(c.loads[p], size);
  return c.slots + delta <= c.slotBudget;
}

// Would trading `outAmt` of product `pOut` for `inAmt` of `pIn` keep it legal?
function canSwap(c, pOut, outAmt, pIn, inAmt) {
  if (pOut === pIn) {
    const size = c.sizes[pOut];
    const next = c.loads[pOut] - outAmt + inAmt;
    return c.slots + slotsFor(next, size) - slotsFor(c.loads[pOut], size) <= c.slotBudget;
  }
  const outSize = c.sizes[pOut];
  const inSize = c.sizes[pIn];
  const delta =
    slotsFor(c.loads[pOut] - outAmt, outSize) - slotsFor(c.loads[pOut], outSize) +
    slotsFor(c.loads[pIn] + inAmt, inSize) - slotsFor(c.loads[pIn], inSize);
  return c.slots + delta <= c.slotBudget;
}

// ---- Fleet ----

// Reads one raw row into a tier, or null if it carries nothing usable.
function readTier(raw) {
  if (raw == null) return null;

  const rawCount = raw.count;
  const parsedCount = Math.floor(toNum(rawCount));
  const count =
    rawCount == null || rawCount === '' || parsedCount <= 0 ? Infinity : parsedCount;

  const toteSlots = Math.floor(toNum(raw.toteSlots));
  const capacity = toNum(raw.capacity);
  // A row with no explicit mode is a tanker when it has litres, and a deck when
  // it only has tote slots — so fleets saved before decks existed still read.
  const mode = raw.mode === DECK || (raw.mode == null && capacity <= 0 && toteSlots > 0)
    ? DECK
    : TANKER;

  if (mode === DECK) {
    if (toteSlots <= 0) return null;
    return { mode: DECK, toteSlots, capacity: null, compartments: null, count };
  }

  if (capacity <= 0) return null;
  const parsedComp = Math.floor(toNum(raw.compartments));
  return {
    mode: TANKER,
    capacity,
    compartments: parsedComp >= 1 ? parsedComp : 1,
    toteSlots: null,
    count,
  };
}

const tierKey = (t) =>
  t.mode === DECK ? `deck|${t.toteSlots}` : `tanker|${t.capacity}|${t.compartments}`;

// Slots one truck of this tier offers per run.
const slotsPerRun = (t) => (t.mode === DECK ? t.toteSlots : t.compartments);

/**
 * Accepts a plain capacity (the original single-capacity call) or a list of
 * tiers. Rows are merged by mode and by the numbers that define them — a
 * 30,000 L single-shell tanker, a 30,000 L three-compartment tanker and an
 * 8-slot deck truck are three different vehicles and never merge — and returned
 * largest-first, tankers ahead of decks at equal size because they can carry
 * anything.
 */
export function normalizeFleet(fleet) {
  const raw = Array.isArray(fleet) ? fleet : [{ capacity: fleet, count: null }];
  const merged = new Map();

  for (const row of raw) {
    const tier = readTier(row);
    if (!tier) continue;

    const key = tierKey(tier);
    const prev = merged.get(key);
    if (!prev) {
      merged.set(key, tier);
    } else if (prev.count !== Infinity) {
      prev.count = tier.count === Infinity ? Infinity : prev.count + tier.count;
    }
  }

  return Array.from(merged.values()).sort(
    (a, b) =>
      slotsPerRun(b) - slotsPerRun(a) ||
      (a.mode === b.mode ? 0 : a.mode === TANKER ? -1 : 1) ||
      (b.capacity || 0) - (a.capacity || 0)
  );
}

// The per-product slot size this tier offers: one shell size for every product
// on a tanker, that product's tote size on a deck (0 = cannot carry).
function sizesForTier(tier, productNames, toteSizes) {
  if (tier.mode === DECK) {
    return productNames.map((name) => Math.max(0, toNum(toteSizes[name])));
  }
  const shell = tier.capacity / tier.compartments;
  return productNames.map(() => shell);
}

/**
 * Fleet arithmetic for the UI (and the planner's own reporting).
 *
 * `slotCapacity` and `trucksAvailable` are exact. The volumetric figures cover
 * **tanker tiers only** — a deck truck's litres depend on which totes it happens
 * to carry, so there is no fixed number to add. `trucksNeeded` is a LOWER BOUND:
 * geography and the radius/site caps routinely push the real count above it.
 *
 * @param {Array|number} fleet        tiers, or a single tanker capacity
 * @param {number} maxTripsPerTruck   runs one truck can make (blank/0 = 1)
 * @param {number} totalUllage        litres to deliver
 * @param {object} [productLoads]     { productName: litres }
 * @param {object} [toteSizes]        { productName: litres per tote }
 */
export function summarizeFleet(fleet, maxTripsPerTruck, totalUllage, productLoads, toteSizes) {
  const tiers = normalizeFleet(fleet);
  const maxTrips = Math.max(1, Math.floor(toNum(maxTripsPerTruck) || 1));
  const unlimited = tiers.some((t) => t.count === Infinity);
  const sizes = toteSizes || {};
  const loads = productLoads || {};

  const tankers = tiers.filter((t) => t.mode === TANKER);
  const hasDeck = tiers.some((t) => t.mode === DECK);
  const volumeUnlimited = tankers.some((t) => t.count === Infinity);

  const capacity = volumeUnlimited
    ? Infinity
    : tankers.reduce((sum, t) => sum + t.capacity * maxTrips * t.count, 0);
  const slotCapacity = unlimited
    ? Infinity
    : tiers.reduce((sum, t) => sum + slotsPerRun(t) * maxTrips * t.count, 0);
  const trucksAvailable = unlimited ? Infinity : tiers.reduce((n, t) => n + t.count, 0);

  // Products with volume but no tote size can only travel by tanker.
  const productNames = Object.keys(loads).filter((name) => toNum(loads[name]) > EPS);
  const tankerOnly = productNames.filter((name) => !(toNum(sizes[name]) > 0));
  const uncarryable = tankers.length ? [] : tankerOnly;

  // Volumetric walk, biggest tanker first — the fewest vehicles for the volume.
  let need = Math.max(0, toNum(totalUllage));
  let trucksNeeded = 0;
  for (const t of tankers) {
    if (need <= 0) break;
    const effective = t.capacity * maxTrips;
    const wanted = Math.ceil(need / effective);
    const use = t.count === Infinity ? wanted : Math.min(t.count, wanted);
    trucksNeeded += use;
    need -= use * effective;
  }

  // Slot floor: the fewest trucks any single tier could do the whole job in.
  // Both are lower bounds, so the larger of the two still is.
  let slotsNeeded = 0;
  if (productNames.length && tiers.length) {
    let best = Infinity;
    for (const t of tiers) {
      const tierSizes = sizesForTier(t, productNames, sizes);
      const s = productNames.reduce(
        (n, name, i) => n + slotsFor(toNum(loads[name]), tierSizes[i]),
        0
      );
      if (s === Infinity) continue;
      const trucks = Math.ceil(s / (slotsPerRun(t) * maxTrips));
      if (trucks < best) {
        best = trucks;
        slotsNeeded = s;
      }
    }
    if (best !== Infinity) trucksNeeded = Math.max(trucksNeeded, best);
  }

  return {
    tiers,
    unlimited,
    hasDeck,
    maxTripsPerTruck: maxTrips,
    capacity,
    slotCapacity,
    trucksAvailable,
    trucksNeeded,
    productCount: productNames.length,
    slotsNeeded,
    tankerOnly,
    uncarryable,
    // Volumetric, and therefore tanker-only. With decks in the fleet the real
    // limit is usually slots, not litres.
    shortfall: Math.max(0, need),
  };
}

// Hands out trucks largest-first and takes them back when one ends up empty.
// `nextFor` refuses a tier that cannot carry any of the sites still waiting,
// which is what stops a deck truck being spent on a product it has no tote for.
function makeSlotPool(tiers, maxTrips, productNames, toteSizes) {
  const pool = tiers.map((t) => {
    const sizes = sizesForTier(t, productNames, toteSizes);
    const perRun = slotsPerRun(t);
    return {
      key: tierKey(t),
      mode: t.mode,
      capacity: t.capacity,
      compartments: t.compartments,
      toteSlots: t.toteSlots,
      sizes,
      slotsPerRun: perRun,
      slotBudget: perRun * maxTrips,
      cap: t.mode === TANKER ? t.capacity * maxTrips : null,
      remaining: t.count,
    };
  });

  const issue = (p) => {
    if (p.remaining !== Infinity) p.remaining -= 1;
    return {
      mode: p.mode,
      capacity: p.capacity,
      compartments: p.compartments,
      toteSlots: p.toteSlots,
      sizes: p.sizes,
      slotsPerRun: p.slotsPerRun,
      slotBudget: p.slotBudget,
      cap: p.cap,
    };
  };

  return {
    next() {
      for (const p of pool) {
        if (p.remaining > 0) return issue(p);
      }
      return null;
    },
    // The first tier with stock that can carry at least one waiting site.
    nextFor(remaining, sites) {
      for (const p of pool) {
        if (p.remaining <= 0) continue;
        if (remaining.some((i) => p.sizes[sites[i].p] > 0)) return issue(p);
      }
      return null;
    },
    release(key) {
      const p = pool.find((x) => x.key === key);
      if (p && p.remaining !== Infinity) p.remaining += 1;
    },
    hasSlot() {
      return pool.some((p) => p.remaining > 0);
    },
  };
}

// Re-badge every finished truck with the smallest vehicle that can still carry
// its load. Building always grabs the biggest truck available, which would
// otherwise label a 4,000 L cluster as a 30,000 L tanker.
//
// Caveat: with one product and one tanker size this was a total order, and
// heaviest-first provably never stranded a truck. Mixed modes and compartment
// counts break that ordering, so this is a heuristic. It never invalidates a
// plan — the grouping is untouched, and a truck it cannot satisfy keeps the
// largest available badge and is reported with `overCapacity`.
function assignFleet(clusters, tiers, maxTrips, productNames, toteSizes) {
  const supply = tiers
    .map((t) => {
      const sizes = sizesForTier(t, productNames, toteSizes);
      const perRun = slotsPerRun(t);
      return {
        key: tierKey(t),
        mode: t.mode,
        capacity: t.capacity,
        compartments: t.compartments,
        toteSlots: t.toteSlots,
        sizes,
        slotsPerRun: perRun,
        slotBudget: perRun * maxTrips,
        cap: t.mode === TANKER ? t.capacity * maxTrips : null,
        remaining: t.count,
      };
    })
    .sort((a, b) => a.slotsPerRun - b.slotsPerRun || (a.capacity || 0) - (b.capacity || 0));
  if (!supply.length) return;

  const heaviestFirst = clusters
    .map((_, i) => i)
    .sort((a, b) => clusters[b].load - clusters[a].load);

  for (const i of heaviestFirst) {
    const cluster = clusters[i];
    // Smallest that fits; failing that, the largest that can at least carry the
    // load. A tier that cannot carry it is never an option -- a deck truck with
    // no tote for one of these products would turn a valid plan into an
    // impossible one. When nothing is left that can carry it, the cluster keeps
    // the truck it was built on, which by construction could.
    const pick =
      supply.find((s) => s.remaining > 0 && slotsOf(cluster.loads, s.sizes) <= s.slotBudget) ||
      supply
        .filter((s) => s.remaining > 0 && slotsOf(cluster.loads, s.sizes) !== Infinity)
        .pop();
    if (!pick) {
      const own = supply.find((s) => s.key === cluster.key);
      if (own && own.remaining !== Infinity && own.remaining > 0) own.remaining -= 1;
      continue;
    }
    if (pick.remaining !== Infinity && pick.remaining > 0) pick.remaining -= 1;

    cluster.key = pick.key;
    cluster.mode = pick.mode;
    cluster.capacity = pick.capacity;
    cluster.compartments = pick.compartments;
    cluster.toteSlots = pick.toteSlots;
    cluster.sizes = pick.sizes;
    cluster.slotsPerRun = pick.slotsPerRun;
    cluster.slotBudget = pick.slotBudget;
    cluster.cap = pick.cap;
    cluster.slots = slotsOf(cluster.loads, cluster.sizes);
  }
}

function boundsOf(members) {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  members.forEach((m) => {
    minLat = Math.min(minLat, m.lat);
    maxLat = Math.max(maxLat, m.lat);
    minLng = Math.min(minLng, m.lng);
    maxLng = Math.max(maxLng, m.lng);
  });
  return { minLat, maxLat, minLng, maxLng };
}

// Seed one truck at a peripheral (or random) site this truck can actually carry,
// then keep adding the nearest eligible site until nothing else fits. Mutates
// `remaining`. Returns null when the truck can carry none of what is left.
function growCluster(sites, remaining, slot, maxSites, maxRadiusKm, rng, productCount) {
  const carryable = [];
  for (let k = 0; k < remaining.length; k++) {
    if (slot.sizes[sites[remaining[k]].p] > 0) carryable.push(k);
  }
  if (!carryable.length) return null;

  let seedPos;
  if (rng) {
    seedPos = carryable[Math.floor(rng() * carryable.length)];
  } else {
    // Farthest from the centre of what's left → compact clusters.
    const c = centroidOfIdx(sites, remaining);
    let bestD = -1;
    seedPos = carryable[0];
    for (const k of carryable) {
      const d = haversineKm(sites[remaining[k]], c);
      if (d > bestD) {
        bestD = d;
        seedPos = k;
      }
    }
  }

  const seedIdx = remaining.splice(seedPos, 1)[0];
  const cluster = makeCluster(slot, productCount);
  cluster.key = slot.key;
  cluster.idx.push(seedIdx);
  addLoad(cluster, sites[seedIdx].p, sites[seedIdx].ullage);
  let centre = { lat: sites[seedIdx].lat, lng: sites[seedIdx].lng };

  while (cluster.idx.length < maxSites) {
    let bestK = -1;
    let bestD = Infinity;
    for (let k = 0; k < remaining.length; k++) {
      const cand = sites[remaining[k]];
      if (!canAdd(cluster, cand.p, cand.ullage)) continue;
      const d = haversineKm(cand, centre);
      if (d > maxRadiusKm) continue;
      if (d < bestD) {
        bestD = d;
        bestK = k;
      }
    }
    if (bestK === -1) break;
    const chosen = remaining.splice(bestK, 1)[0];
    cluster.idx.push(chosen);
    addLoad(cluster, sites[chosen].p, sites[chosen].ullage);
    centre = centroidOfIdx(sites, cluster.idx);
  }

  return cluster;
}

// One greedy build: take trucks from the pool and fill each one, until the sites
// run out or the fleet does.
function buildClusters(sites, pool, maxSites, maxRadiusKm, rng, productCount) {
  const remaining = sites.map((_, i) => i);
  const clusters = [];

  while (remaining.length) {
    const slot = pool.nextFor(remaining, sites);
    if (!slot) break;
    const cluster = growCluster(sites, remaining, slot, maxSites, maxRadiusKm, rng, productCount);
    // nextFor guarantees a carryable seed exists, so this is belt and braces.
    if (!cluster) {
      pool.release(slot.key);
      break;
    }
    clusters.push(cluster);
  }

  return { clusters, unplaced: remaining };
}

// Give stranded sites another chance: squeeze them into a truck that still has
// room, then open fresh trucks for whatever is left.
function placeLeftovers(sites, clusters, unplaced, pool, maxSites, maxRadiusKm, productCount) {
  if (!unplaced.length) return unplaced;

  let left = [];
  for (const s of unplaced) {
    const nearest = clusters
      .map((c, b) => ({ b, d: haversineKm(sites[s], centroidOfIdx(sites, c.idx)) }))
      .sort((x, y) => x.d - y.d);

    let placed = false;
    for (const { b } of nearest) {
      const C = clusters[b];
      if (!canAdd(C, sites[s].p, sites[s].ullage)) continue;
      if (C.idx.length >= maxSites) continue;
      const withS = C.idx.concat(s);
      if (!radiusOk(sites, withS, maxRadiusKm)) continue;
      C.idx = withS;
      addLoad(C, sites[s].p, sites[s].ullage);
      placed = true;
      break;
    }
    if (!placed) left.push(s);
  }

  while (left.length && pool.hasSlot()) {
    const slot = pool.nextFor(left, sites);
    if (!slot) break;
    const cluster = growCluster(sites, left, slot, maxSites, maxRadiusKm, null, productCount);
    if (!cluster) {
      pool.release(slot.key);
      break;
    }
    clusters.push(cluster);
  }

  return left;
}

// Nearest `count` clusters to a point, by centroid distance, excluding `skip`.
function nearestClusters(point, centroids, skip, count) {
  const ranked = [];
  for (let b = 0; b < centroids.length; b++) {
    if (b === skip) continue;
    ranked.push({ b, d: haversineKm(point, centroids[b]) });
  }
  ranked.sort((x, y) => x.d - y.d);
  return ranked.slice(0, count).map((r) => r.b);
}

// Local search: relocate single sites and swap pairs between nearby clusters,
// keeping only moves that reduce total compactness and respect the caps.
function localSearch(sites, clusters, maxSites, maxRadiusKm, passes, pool) {
  const allowSwap = sites.length <= 800;

  for (let pass = 0; pass < passes; pass++) {
    let improved = false;
    let centroids = clusters.map((c) => centroidOfIdx(sites, c.idx));

    // ---- Relocate ----
    for (let a = 0; a < clusters.length; a++) {
      const A = clusters[a];
      for (let mi = A.idx.length - 1; mi >= 0; mi--) {
        const s = A.idx[mi];
        const aWithout = A.idx.filter((_, k) => k !== mi);
        const aCostNow = clusterCost(sites, A.idx);
        const aCostWithout = clusterCost(sites, aWithout);

        const candidates = nearestClusters(sites[s], centroids, a, RELOCATE_CANDIDATES);
        let bestB = -1;
        let bestDelta = -1e-9;
        for (const b of candidates) {
          const B = clusters[b];
          if (!canAdd(B, sites[s].p, sites[s].ullage)) continue;
          if (B.idx.length >= maxSites) continue;
          const bWith = B.idx.concat(s);
          if (!radiusOk(sites, bWith, maxRadiusKm)) continue;
          const delta =
            aCostWithout + clusterCost(sites, bWith) - (aCostNow + clusterCost(sites, B.idx));
          if (delta < bestDelta) {
            bestDelta = delta;
            bestB = b;
          }
        }

        if (bestB >= 0) {
          const B = clusters[bestB];
          A.idx = A.idx.filter((_, k) => k !== mi);
          removeLoad(A, sites[s].p, sites[s].ullage);
          B.idx = B.idx.concat(s);
          addLoad(B, sites[s].p, sites[s].ullage);
          centroids[a] = A.idx.length ? centroidOfIdx(sites, A.idx) : centroids[a];
          centroids[bestB] = centroidOfIdx(sites, B.idx);
          improved = true;
        }
      }
    }

    // Drop any clusters emptied by relocation and hand the truck back.
    for (let i = clusters.length - 1; i >= 0; i--) {
      if (clusters[i].idx.length) continue;
      if (pool) pool.release(clusters[i].key);
      clusters.splice(i, 1);
    }

    // ---- Swap ----
    if (allowSwap) {
      centroids = clusters.map((c) => centroidOfIdx(sites, c.idx));
      for (let a = 0; a < clusters.length; a++) {
        const neighbours = nearestClusters(centroids[a], centroids, a, SWAP_CANDIDATES);
        for (const b of neighbours) {
          if (b <= a) continue;
          const A = clusters[a];
          const B = clusters[b];
          const aCost = clusterCost(sites, A.idx);
          const bCost = clusterCost(sites, B.idx);
          let done = false;
          for (let i = 0; i < A.idx.length && !done; i++) {
            for (let j = 0; j < B.idx.length; j++) {
              const s = A.idx[i];
              const t = B.idx[j];
              if (!canSwap(A, sites[s].p, sites[s].ullage, sites[t].p, sites[t].ullage)) continue;
              if (!canSwap(B, sites[t].p, sites[t].ullage, sites[s].p, sites[s].ullage)) continue;
              const newA = A.idx.slice();
              newA[i] = t;
              const newB = B.idx.slice();
              newB[j] = s;
              if (!radiusOk(sites, newA, maxRadiusKm)) continue;
              if (!radiusOk(sites, newB, maxRadiusKm)) continue;
              const delta = clusterCost(sites, newA) + clusterCost(sites, newB) - (aCost + bCost);
              if (delta < -1e-9) {
                A.idx = newA;
                removeLoad(A, sites[s].p, sites[s].ullage);
                addLoad(A, sites[t].p, sites[t].ullage);
                B.idx = newB;
                removeLoad(B, sites[t].p, sites[t].ullage);
                addLoad(B, sites[s].p, sites[s].ullage);
                centroids[a] = centroidOfIdx(sites, A.idx);
                centroids[b] = centroidOfIdx(sites, B.idx);
                improved = true;
                done = true;
                break;
              }
            }
          }
        }
      }
    }

    if (!improved) break;
  }

  return clusters;
}

function totalCost(sites, clusters) {
  let sum = 0;
  for (const c of clusters) sum += clusterCost(sites, c.idx);
  return sum;
}

/**
 * @param {Array} rawSites sites with latitude, longitude, fillAmount, product, containerId
 * @param {Array|number} fleet tiers — tankers `{ mode:'tanker', capacity, compartments, count }`
 *   and/or deck trucks `{ mode:'deck', toteSlots, count }`. Blank/zero `count` = unlimited.
 *   A bare number is one unlimited single-compartment tanker.
 * @param {object} [options]
 * @param {object} [options.toteSizes]        { productName: litres per tote } from the Supply step
 * @param {number} [options.maxRadiusKm]      max km from a truck's centre to any of its sites (0/blank = no limit)
 * @param {number} [options.maxSitesPerTruck] max sites per truck (0/blank = no limit)
 * @param {number} [options.maxTripsPerTruck] runs one truck can make; multiplies its slots (blank/0 = 1)
 * @param {number} [options.optimizeEffort]   0–100: higher = more restarts + local search
 * @returns {object}
 */
// Spread a fixed total across sites, each held inside its own [min, max].
//
// Every site starts at its minimum (so no site is short-changed), then the
// surplus is shared out in proportion to each site's headroom (max - min) --
// a site with more room to spare absorbs more of it. Litres are whole numbers,
// so the floored shares are topped up one litre at a time, largest fractional
// share first, which keeps the sum exactly equal to `total`.
function distributeWithinRanges(mins, maxs, total) {
  const amounts = mins.slice();
  const sumMin = mins.reduce((a, b) => a + b, 0);
  let surplus = Math.round(total - sumMin);
  if (surplus <= 0) return amounts;

  const headroom = maxs.map((m, i) => Math.max(0, m - mins[i]));
  const totalHeadroom = headroom.reduce((a, b) => a + b, 0);
  if (totalHeadroom <= 0) return amounts;

  const exact = headroom.map((h) => (surplus * h) / totalHeadroom);
  const share = exact.map((v) => Math.floor(v));
  let given = share.reduce((a, b) => a + b, 0);

  // Remaining litres to the largest fractional shares, skipping anything
  // already at its ceiling. Cycles because a skipped site pushes its litre on.
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac)
    .map((e) => e.i);

  while (given < surplus) {
    let progressed = false;
    for (const i of order) {
      if (given >= surplus) break;
      if (share[i] < headroom[i]) {
        share[i] += 1;
        given += 1;
        progressed = true;
      }
    }
    // No site can take more; the rest cannot be placed.
    if (!progressed) break;
  }

  for (let i = 0; i < amounts.length; i += 1) {
    amounts[i] = mins[i] + Math.min(share[i], headroom[i]);
  }
  return amounts;
}

// Decide the drops for one product's sites so the totes loaded come back empty.
//
// A tote is NOT per site -- it is filled from across several. So wholeness is a
// property of the product's TOTAL: pick the largest whole number of totes that
// still fits under the sum of the sites' maximums, provided it covers the sum of
// their minimums, then share that total out within each site's range.
//
//   3 sites, minimums summing to 900 L, a 1,200 L tote, plenty of headroom
//   -> load 1 full tote and spread the extra 300 L across the three.
//
// A partial tote is only used when no whole number of totes lands inside
// [sum of minimums, sum of maximums] -- then the total is the sum of maximums
// and the last tote goes back part-used.
export function allocateWholeTotes(entries, toteSize) {
  const size = toNum(toteSize);
  const mins = (entries || []).map((e) => Math.max(0, Math.round(toNum(e.min))));
  const maxs = (entries || []).map((e, i) => Math.max(mins[i], Math.round(toNum(e.max))));
  const sumMin = mins.reduce((a, b) => a + b, 0);
  const sumMax = maxs.reduce((a, b) => a + b, 0);

  // No usable tote size, or nothing to deliver: leave the amounts alone.
  if (!(size > 0) || !(sumMax > 0)) {
    return { amounts: maxs.slice(), total: sumMax, totes: 0, fullTotes: true };
  }

  const wholeTotes = Math.floor(sumMax / size);
  const canFillWhole = wholeTotes >= 1 && wholeTotes * size >= sumMin;

  const total = canFillWhole ? wholeTotes * size : sumMax;
  const totes = canFillWhole ? wholeTotes : Math.ceil(sumMax / size);

  return {
    amounts: distributeWithinRanges(mins, maxs, total),
    total,
    totes,
    fullTotes: canFillWhole,
  };
}

export function planDeliveries(rawSites, fleet, options = {}) {
  const maxRadiusKm = toNum(options.maxRadiusKm) || Infinity;
  const maxSites = toNum(options.maxSitesPerTruck) || Infinity;
  const maxTrips = Math.max(1, Math.floor(toNum(options.maxTripsPerTruck) || 1));
  const toteSizes = options.toteSizes || {};
  const effort =
    options.optimizeEffort == null ? 60 : Math.max(0, Math.min(100, toNum(options.optimizeEffort)));

  // On a tote run each drop is rounded to whole totes where the site's delivery
  // range allows it, so the plan is built from the amounts that will actually be
  // loaded rather than from the top of every range.
  const wholeTotes = options.wholeTotes === true;

  const located = (rawSites || [])
    .map((s) => {
      const maxAmount = toNum(s.fillAmount);
      return {
        ref: s,
        containerId: s.containerId,
        lat: toNum(s.latitude),
        lng: toNum(s.longitude),
        // Replaced below on a tote run; until then a site takes the top of its
        // range, which is the behaviour a tanker run keeps.
        ullage: maxAmount,
        minAmount: toNum(s.minFillAmount),
        maxAmount,
        product: normalizeProduct(s.product),
      };
    })
    .filter((s) => s.lat !== 0 && s.lng !== 0);

  // On a tote run the drops are decided per PRODUCT, not per site: a tote is
  // filled from across several sites, so the product's total is what has to
  // come to a whole number of totes. Each site then takes a share of that total
  // inside its own [min, max].
  const toteAllocation = {};
  if (wholeTotes) {
    const byProduct = new Map();
    for (const site of located) {
      let group = byProduct.get(site.product);
      if (!group) {
        group = [];
        byProduct.set(site.product, group);
      }
      group.push(site);
    }
    byProduct.forEach((group, product) => {
      const result = allocateWholeTotes(
        group.map((site) => ({ min: site.minAmount, max: site.maxAmount })),
        toteSizes[product]
      );
      group.forEach((site, i) => {
        site.ullage = result.amounts[i];
      });
      toteAllocation[product] = {
        total: result.total,
        totes: result.totes,
        fullTotes: result.fullTotes,
        toteSize: toNum(toteSizes[product]) || null,
      };
    });
  }

  // Index products only over sites that made it in, so a discarded site can't
  // leave a phantom product in the breakdown.
  // Sites go back out as the caller's own object plus what planning decided:
  // the amount actually loaded for this site, how many totes that is, and
  // whether whole totes covered it. Spread, so every original field survives.
  const emitSite = (s) => ({
    ...s.ref,
    plannedAmount: s.ullage,
    wholeToteRounding: wholeTotes,
  });

  const productNames = [];
  const productIndex = new Map();
  const sites = located.map((s) => {
    let p = productIndex.get(s.product);
    if (p === undefined) {
      p = productNames.length;
      productNames.push(s.product);
      productIndex.set(s.product, p);
    }
    return { ...s, p };
  });
  const productCount = productNames.length;

  const productTotals = {};
  productNames.forEach((name) => {
    productTotals[name] = 0;
  });
  for (const s of sites) productTotals[s.product] += s.ullage;

  const totalUllage = sites.reduce((sum, s) => sum + s.ullage, 0);
  const summary = summarizeFleet(fleet, maxTrips, totalUllage, productTotals, toteSizes);
  const tiers = summary.tiers;

  // Echo the fleet back in the plain shape the UI stores (null = unlimited).
  const fleetOut = tiers.map((t) => ({
    mode: t.mode,
    capacity: t.capacity,
    compartments: t.compartments,
    toteSlots: t.toteSlots,
    count: t.count === Infinity ? null : t.count,
  }));
  const base = {
    // Per-product tote allocation on a tote run: the litres planned, how many
    // totes that is, and whether they all come back empty. Empty on a tanker run.
    toteAllocation,
    fleet: fleetOut,
    fleetCapacity: summary.capacity === Infinity ? null : summary.capacity,
    slotCapacity: summary.slotCapacity === Infinity ? null : summary.slotCapacity,
    trucksAvailable: summary.trucksAvailable === Infinity ? null : summary.trucksAvailable,
    hasDeck: summary.hasDeck,
    largestCapacity: tiers.reduce((m, t) => Math.max(m, t.capacity || 0), 0),
    totalUllage,
    products: productNames.slice(),
    productTotals,
    toteSizes: { ...toteSizes },
    // Products with volume but no tote size; they can only travel by tanker.
    tankerOnly: summary.tankerOnly,
    minTrucks: summary.trucksNeeded,
    shortfall: summary.shortfall,
    maxRadiusKm: options.maxRadiusKm ? maxRadiusKm : 0,
    maxSitesPerTruck: options.maxSitesPerTruck ? maxSites : 0,
    maxTripsPerTruck: maxTrips,
    optimizeEffort: effort,
  };

  if (!tiers.length || !sites.length) {
    return {
      ...base,
      truckCount: 0,
      plannedUllage: 0,
      trucks: [],
      unplaced: sites.length,
      unplacedSites: sites.map(emitSite),
    };
  }

  // Effort → work budget, scaled down for large inputs to stay responsive.
  let restarts = effort <= 0 ? 1 : Math.max(1, Math.round((effort / 100) * 20));
  let passes = effort <= 0 ? 0 : Math.max(2, Math.round((effort / 100) * 8));
  if (sites.length > 800) {
    restarts = Math.min(restarts, 5);
    passes = Math.min(passes, 4);
  }
  if (sites.length > 2000) {
    restarts = 1;
    passes = Math.min(passes, 2);
  }

  // Best = fewest stranded sites, then tightest.
  let best = null;
  let bestUnplaced = Infinity;
  let bestCost = Infinity;
  for (let r = 0; r < restarts; r++) {
    const rng = r === 0 ? null : makeRng((Math.imul(r, 2654435761) >>> 0) || 1);
    const pool = makeSlotPool(tiers, maxTrips, productNames, toteSizes);
    const built = buildClusters(sites, pool, maxSites, maxRadiusKm, rng, productCount);
    const clusters = built.clusters;
    if (passes > 0) localSearch(sites, clusters, maxSites, maxRadiusKm, passes, pool);
    const unplaced = placeLeftovers(
      sites, clusters, built.unplaced, pool, maxSites, maxRadiusKm, productCount
    );
    assignFleet(clusters, tiers, maxTrips, productNames, toteSizes);

    const cost = totalCost(sites, clusters);
    if (unplaced.length < bestUnplaced || (unplaced.length === bestUnplaced && cost < bestCost)) {
      bestUnplaced = unplaced.length;
      bestCost = cost;
      best = { clusters, unplaced };
    }
  }

  const trucks = best.clusters.map((c, idx) => {
    const members = c.idx.map((i) => sites[i]);
    const centre = centroidOfIdx(sites, c.idx);
    const load = members.reduce((s, m) => s + m.ullage, 0);
    const radiusKm = members.reduce((max, m) => Math.max(max, haversineKm(m, centre)), 0);

    // What this truck actually loads, heaviest product first.
    const productBreakdown = productNames
      .map((name, p) => ({
        product: name,
        load: c.loads[p],
        slotSize: c.sizes[p],
        compartments: slotsFor(c.loads[p], c.sizes[p]),
      }))
      .filter((entry) => entry.load > EPS)
      .sort((a, b) => b.load - a.load);

    const slotsUsed = c.slots;
    const finite = slotsUsed !== Infinity;

    return {
      id: idx + 1,
      color: TRUCK_COLORS[idx % TRUCK_COLORS.length],
      sites: members.map(emitSite),
      load,
      mode: c.mode,
      capacity: c.capacity,
      effectiveCapacity: c.cap,
      compartments: c.mode === TANKER ? c.compartments : c.toteSlots,
      toteSlots: c.toteSlots,
      slotsPerRun: c.slotsPerRun,
      compartmentsUsed: finite ? slotsUsed : null,
      compartmentBudget: c.slotBudget,
      products: productBreakdown,
      productCount: productBreakdown.length,
      // Volumetric only means something for a tanker; a deck truck's fullness is
      // how many of its slots are taken.
      utilization: c.mode === TANKER && c.cap > 0 ? load / c.cap : null,
      slotUtilization: finite && c.slotBudget > 0 ? slotsUsed / c.slotBudget : null,
      trips: finite ? Math.max(1, Math.ceil(slotsUsed / c.slotsPerRun)) : null,
      overCapacity: !finite || slotsUsed > c.slotBudget,
      bounds: boundsOf(members),
      centroid: centre,
      radiusKm,
    };
  });

  return {
    ...base,
    truckCount: trucks.length,
    plannedUllage: trucks.reduce((sum, t) => sum + t.load, 0),
    trucks,
    unplaced: best.unplaced.length,
    unplacedSites: best.unplaced.map((i) => emitSite(sites[i])),
  };
}
